import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

export default function PetitionCreate() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", addressed_to: "", action_requested: "", problem: "", justification: "",
    sources: "", territory: "", deadline: "", creator_public: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Inicia sesión para proponer una petición."); login(); return; }
    if (form.title.trim().length < 6 || form.justification.trim().length < 20) {
      toast.error("Completa el título y una justificación más detallada."); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        sources: form.sources.split("\n").map((s) => s.trim()).filter(Boolean),
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      };
      const r = await api.post("/petitions", payload);
      toast.success(r.data.message);
      navigate("/peticiones");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la petición.");
    } finally { setBusy(false); }
  };

  const F = ({ label, k, type = "text", placeholder, area }) => (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      {area ? (
        <textarea value={form[k]} onChange={(e) => set(k, e.target.value)} rows={4} data-testid={`petition-${k}`}
          placeholder={placeholder} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-cyan-500" />
      ) : (
        <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`petition-${k}`}
          placeholder={placeholder} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-cyan-500" />
      )}
    </div>
  );

  return (
    <div data-testid="petition-create-page">
      <PageHero label="2Voto Peticiones" title="Crear una petición ciudadana"
        subtitle="Toda petición debe solicitar una acción concreta, identificar al destinatario e incluir justificación y fuentes. Nada se publica automáticamente: el equipo de 2Voto revisa cada petición." />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 mb-8">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">No afirmes culpabilidad sin evidencia ni incluyas amenazas, insultos o datos privados. Las peticiones relacionadas con investigaciones deben respetar la presunción de inocencia.</p>
        </div>
        <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-5" data-testid="petition-form">
          <F label="Título propuesto *" k="title" placeholder="¿Qué se solicita, en pocas palabras?" />
          <F label="Persona o institución destinataria *" k="addressed_to" placeholder="Ej. Gobernadora de Puerto Rico" />
          <F label="Acción concreta solicitada *" k="action_requested" placeholder="Ej. Renuncia o separación temporal" />
          <F label="Problema *" k="problem" area placeholder="¿Cuál es la situación?" />
          <F label="Justificación *" k="justification" area placeholder="Fundamento de la solicitud (mínimo 20 caracteres)" />
          <F label="Fuentes / enlaces (una por línea)" k="sources" area placeholder="https://…" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Territorio afectado" k="territory" placeholder="Ej. Puerto Rico / San Juan" />
            <F label="Fecha límite" k="deadline" type="date" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.creator_public} onChange={(e) => set("creator_public", e.target.checked)} data-testid="petition-creator-public" className="w-4 h-4 accent-cyan-500" />
            <span className="text-sm text-slate-700">Mostrar mi identidad como creador (por defecto permanece confidencial)</span>
          </label>
          <button type="submit" disabled={busy} data-testid="petition-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-rose-500 text-white font-semibold py-3 rounded-full hover:bg-rose-600 transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> {busy ? "Enviando…" : "Enviar para revisión"}
          </button>
        </form>
      </div>
    </div>
  );
}
